import { base64ToBytes } from '../net/base64';

/**
 * Plays the assistant's own streamed speech as raw LINEAR16 PCM chunks arrive — the
 * streaming counterpart to `AssistantController.playNext`'s `new Audio(data:...)` clip
 * playback, used instead of it for whichever utterance the server's streaming text-to-speech
 * path actually carried (`AssistantPipeline.SpeakActSpeakAsync`'s own "Streaming first"
 * remarks: today, only ever the turn's first utterance — the acknowledgement, or the whole
 * reply when there is no tool call).
 *
 * **Why not `MediaSource`, unlike `PeerVoicePlayer.ts`.** `PeerVoicePlayer` is the right
 * precedent to follow for the *shape* of chunked playback — start/chunk/end, one active
 * stream, never play this client's own voice back — and this class follows it in exactly
 * that shape. But `MediaSource`/`SourceBuffer` demux a real container format (WebM, MP4, MP3)
 * incrementally; there is no MIME type `MediaSource.isTypeSupported` (the discipline
 * `PeerVoicePlayer` established: checked, never assumed) could even be asked about for
 * uncontained LINEAR16 PCM, because LINEAR16 is exactly that — headerless raw samples, no
 * container at all. `StreamingTextToSpeechClient` requests LINEAR16 rather than a
 * streamable container specifically because it decodes trivially and deterministically
 * against one known, fixed sample rate (`AssistantSpeechStartedDto.sampleRateHertz`) with no
 * demuxer involved — the same reasoning that already justifies LINEAR16 for the
 * duration-reading acknowledgement clip on the unary path (`Linear16Audio`'s own remarks).
 * Raw `AudioBuffer` scheduling through the Web Audio API is the standard technique for
 * playing headerless PCM progressively, and needs nothing from `MediaSource` at all.
 *
 * **Never plays this browser's own voice back** is not a concern here at all, unlike
 * `PeerVoicePlayer`: the assistant is not this client, so there is no "self" to filter out —
 * every peer in the room, including whoever asked, hears the same broadcast
 * (`AssistantPipeline.BroadcastUtteranceAsync`'s own remarks; the streaming path keeps the
 * identical whole-room broadcast convention).
 *
 * No Scene or Mesh dependency, the same separation `AssistantController`/`PeerVoicePlayer`
 * already keep: this class owns one shared `AudioContext` and plain Web Audio nodes, a caller
 * in `AssistantController` wires it to the hub's
 * `assistantSpeechStarted`/`assistantSpeechChunk`/`assistantSpeechEnded` events.
 */

/** The browser surface this class needs, factored out so tests can supply fakes — jsdom has
 *  no real `AudioContext` scheduling, the same constraint `PlaybackAmplitude`/`PeerVoicePlayer`
 *  already document. */
export interface SpeechAudioDeps {
  createAudioContext: () => AudioContext;
}

const REAL_DEPS: SpeechAudioDeps = {
  createAudioContext: () => new AudioContext(),
};

interface StreamEntry {
  sampleRateHertz: number;
  /** Decoded PCM buffered so far, before `play()` has been called for this entry — see
   *  `play()`'s own remarks for why chunks can arrive before playback is allowed to begin. */
  buffered: Float32Array[];
  /** Set once `end()` has been called: no more chunks are coming. */
  ended: boolean;
  /** Set once `play()` has been called: chunks from here on are scheduled immediately
   *  instead of buffered. */
  playing: boolean;
  nextStartTime: number;
  activeSources: number;
  onStarted: (() => void) | null;
  onEnded: (() => void) | null;
  startedFired: boolean;
  /** Set once this utterance is waiting only on scheduled audio to finish — see
   *  `armWatchdog`. Cleared the moment it finishes for real. */
  watchdog: ReturnType<typeof setTimeout> | null;
  /** Armed on `play()` and never dependent on any later event arriving — see `play`. */
  ceiling: ReturnType<typeof setTimeout> | null;
}

/** The absolute ceiling on a single utterance, from the moment it starts playing. Deliberately
 *  far longer than any utterance this assistant produces (`GeminiClient`'s own prompt asks for
 *  a sentence or two, and the longest measured clip is under ten seconds), because this is the
 *  backstop that fires when something is genuinely wrong, not a scheduling parameter. */
const MAX_UTTERANCE_MS = 30_000;

/** How long past an utterance's own scheduled end to wait before giving up on the `'ended'`
 *  events and releasing the caller's queue anyway. Generous, because overshooting merely
 *  delays the next utterance slightly, where undershooting would cut off real speech. */
const WATCHDOG_GRACE_MS = 2000;

export class AssistantSpeechPlayer {
  private ctx: AudioContext | null = null;
  private readonly entries = new Map<number, StreamEntry>();

  constructor(private readonly deps: SpeechAudioDeps = REAL_DEPS) {}

  /**
   * One utterance's speech is about to arrive. Does not start any audible playback by
   * itself — see `play()` — only opens a buffer for `appendChunk` to write into, so a
   * caller's own playback queue can gate *when* this utterance is allowed to be heard
   * independently of when its chunks happen to arrive over the wire.
   */
  start(utteranceIndex: number, sampleRateHertz: number): void {
    this.entries.set(utteranceIndex, {
      sampleRateHertz,
      buffered: [],
      ended: false,
      playing: false,
      nextStartTime: 0,
      activeSources: 0,
      onStarted: null,
      onEnded: null,
      startedFired: false,
      watchdog: null,
      ceiling: null,
    });
  }

  /** One chunk of raw LINEAR16 PCM for an already-`start()`-ed utterance. Silently dropped
   *  if there is no open entry for this index — never started, already ended — the same
   *  tolerance `PeerVoicePlayer.appendChunk` already has for a chunk arriving after its own
   *  context is gone. */
  appendChunk(utteranceIndex: number, base64: string): void {
    const entry = this.entries.get(utteranceIndex);

    if (!entry || entry.ended) {
      return;
    }

    const samples = decodeLinear16(base64ToBytes(base64));

    if (entry.playing) {
      this.schedule(utteranceIndex, entry, samples);
    } else {
      entry.buffered.push(samples);
    }
  }

  /** The matching close for `start()`. A no-op for an unknown index, the same tolerance
   *  every other streamed shape in this project already has for a stray call. */
  end(utteranceIndex: number): void {
    const entry = this.entries.get(utteranceIndex);

    if (!entry) {
      return;
    }

    entry.ended = true;
    this.maybeFinish(utteranceIndex, entry);
  }

  /**
   * Called by the queue owner (`AssistantController`) once this utterance reaches the head
   * of its own playback queue — begins actually scheduling audible playback of whatever is
   * already buffered, and of every chunk that arrives from here on. Splitting `start()` from
   * `play()` this way is what lets chunks for a streamed utterance arrive *while a previous
   * turn's clip is still finishing playback* without either overlapping it or being dropped:
   * they queue up in `buffered` until this is called.
   *
   * `onEnded` fires once every scheduled buffer has finished playing *and* `end()` has been
   * called — never merely because `buffered` is momentarily empty, the same
   * "don't infer done from an empty queue" rule `AssistantController.playNext`'s own module
   * doc already states for the classic clip queue.
   */
  play(utteranceIndex: number, onStarted: () => void, onEnded: () => void): void {
    const entry = this.entries.get(utteranceIndex);

    if (!entry) {
      // Nothing was ever started for this index — tell the caller it is already done rather
      // than hanging its queue forever waiting for an utterance that does not exist.
      onEnded();
      return;
    }

    entry.playing = true;
    entry.onStarted = onStarted;
    entry.onEnded = onEnded;

    // The hard ceiling, armed the moment this utterance starts and independent of every event
    // that might not arrive. armWatchdog's precise deadline is the better guarantee, but it
    // needs end() to have been called to know there is no more audio coming — which makes it
    // useless in exactly the case where `assistantSpeechEnded` is the message going missing.
    // A safety net that depends on the event it is protecting against is not a safety net.
    // This one depends on nothing: whatever happens, the caller's queue is released.
    entry.ceiling = setTimeout(() => {
      entry.ceiling = null;

      if (this.entries.get(utteranceIndex) === entry) {
        console.warn(
          `Assistant speech ${utteranceIndex} exceeded the maximum utterance ceiling ` +
            `(ended=${entry.ended}, activeSources=${entry.activeSources}, ` +
            `audio context state: ${this.ctx?.state ?? 'none'}); releasing the queue anyway.`,
        );
        this.finish(utteranceIndex, entry);
      }
    }, MAX_UTTERANCE_MS);

    const ctx = this.ensureContext();
    entry.nextStartTime = ctx.currentTime;

    const queued = entry.buffered;
    entry.buffered = [];

    for (const samples of queued) {
      this.schedule(utteranceIndex, entry, samples);
    }

    this.maybeFinish(utteranceIndex, entry);
  }

  /** Stops and discards every tracked utterance immediately, for page teardown — the same
   *  "drop anything held" shape `AssistantController.releaseImmediately` and
   *  `PeerVoicePlayer.releaseAll` already use. */
  releaseAll(): void {
    for (const entry of this.entries.values()) {
      if (entry.watchdog !== null) {
        clearTimeout(entry.watchdog);
        entry.watchdog = null;
      }

      if (entry.ceiling !== null) {
        clearTimeout(entry.ceiling);
        entry.ceiling = null;
      }
    }

    this.entries.clear();
  }

  private ensureContext(): AudioContext {
    if (!this.ctx) {
      this.ctx = this.deps.createAudioContext();
    }

    // Shipped once without this and it took the assistant down in production: a browser starts
    // a freshly-constructed AudioContext *suspended* until a user gesture, and a suspended
    // context schedules silently — `source.start()` is accepted, nothing is audible, and the
    // `'ended'` event never fires, so `maybeFinish` never runs and the caller's onEnded never
    // arrives. The room saw the listening light pulse forever while the server logged a
    // perfectly healthy, fully streamed turn. `PlaybackAmplitude` already documented this exact
    // hazard and already called resume(); this class was written next to it and did not.
    //
    // Best-effort and deliberately not awaited, the same shape PlaybackAmplitude uses: resume()
    // rejects when there is still no user gesture to satisfy the autoplay policy, and there is
    // nothing useful to do about that here — the watchdog in armWatchdog() is what keeps a
    // still-silent context from hanging the caller's queue.
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume().catch(() => {});
    }

    return this.ctx;
  }

  private schedule(utteranceIndex: number, entry: StreamEntry, samples: Float32Array): void {
    if (samples.length === 0) {
      return;
    }

    const ctx = this.ensureContext();
    const buffer = ctx.createBuffer(1, samples.length, entry.sampleRateHertz);

    // Same TypeScript-vs-runtime mismatch `PeerVoicePlayer.pump` already documents for
    // `SourceBuffer.appendBuffer`: lib.dom.d.ts types `copyToChannel`'s source against a
    // `Float32Array` backed specifically by `ArrayBuffer`, not the broader `ArrayBufferLike`
    // a plain `new Float32Array(n)` infers as since TS 5.7 — a real browser accepts any
    // typed array view regardless, so this is a type-system mismatch, not a runtime one.
    buffer.copyToChannel(samples as Float32Array<ArrayBuffer>, 0);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    // Scheduled back-to-back off the context's own clock, not off wall-clock arrival time:
    // if a later chunk arrives after the previous one has already finished playing (a
    // network stall), this starts it immediately rather than leaving a gap it does not need
    // to leave.
    const startAt = Math.max(entry.nextStartTime, ctx.currentTime);
    entry.nextStartTime = startAt + buffer.duration;
    entry.activeSources++;

    source.addEventListener('ended', () => {
      entry.activeSources--;
      this.maybeFinish(utteranceIndex, entry);
    });

    source.start(startAt);

    // Approximate — a scheduled `AudioBufferSourceNode` has no `playing` event the way
    // `HTMLMediaElement` does (the event `AssistantController.playNext` waits for, for the
    // same "don't report speaking before anything is audible" reason) — fired once, at the
    // first chunk actually scheduled, which is as close as this mechanism gets.
    if (!entry.startedFired) {
      entry.startedFired = true;
      entry.onStarted?.();
    }
  }

  private maybeFinish(utteranceIndex: number, entry: StreamEntry): void {
    if (entry.playing && entry.ended && entry.activeSources === 0 && entry.buffered.length === 0) {
      this.finish(utteranceIndex, entry);
      return;
    }

    if (entry.playing && entry.ended) {
      this.armWatchdog(utteranceIndex, entry);
    }
  }

  /**
   * The second half of the production failure this class shipped with, and the more important
   * half: `onEnded` was the *only* way out of the caller's speaking state, so anything that
   * stopped the `'ended'` events arriving — a suspended context, a decode failure, a browser
   * quirk on one headset — hung the room permanently, with no recovery short of a reload.
   *
   * Whatever silences the audio must not also silence the *turn*. Once an utterance is playing
   * and no more chunks are coming, its remaining audio has a known duration, so there is a real
   * deadline to hold it to: `nextStartTime` is when the last scheduled buffer ends, on the
   * context's own clock. If nothing has finished by then plus a grace margin, release the
   * caller anyway. Degrading to a silent utterance is recoverable; a stuck queue is not.
   *
   * A suspended context makes this fire by construction rather than by luck: its `currentTime`
   * does not advance at all, so the computed remaining time stays at the full duration and the
   * timer runs out with the entry untouched.
   */
  private armWatchdog(utteranceIndex: number, entry: StreamEntry): void {
    if (entry.watchdog !== null) {
      return;
    }

    const ctx = this.ensureContext();
    const remainingMs = Math.max(0, (entry.nextStartTime - ctx.currentTime) * 1000);

    entry.watchdog = setTimeout(() => {
      entry.watchdog = null;

      // Still tracked means the real 'ended' events never arrived; finish() has already
      // removed it in every healthy case.
      if (this.entries.get(utteranceIndex) === entry) {
        console.warn(
          `Assistant speech ${utteranceIndex} never reported playback finishing ` +
            `(audio context state: ${ctx.state}); releasing the queue anyway.`,
        );
        this.finish(utteranceIndex, entry);
      }
    }, remainingMs + WATCHDOG_GRACE_MS);
  }

  private finish(utteranceIndex: number, entry: StreamEntry): void {
    if (entry.watchdog !== null) {
      clearTimeout(entry.watchdog);
      entry.watchdog = null;
    }

    if (entry.ceiling !== null) {
      clearTimeout(entry.ceiling);
      entry.ceiling = null;
    }

    this.entries.delete(utteranceIndex);
    entry.onEnded?.();
  }
}

/** LINEAR16 is little-endian signed 16-bit PCM — Cloud Text-to-Speech's own documented wire
 *  format for this encoding, the same one `Linear16Audio` reads on the server side (there,
 *  out of a WAV-wrapped unary clip; here, out of a headerless streamed chunk). Normalises to
 *  the [-1, 1] range `AudioBuffer` expects. */
function decodeLinear16(bytes: Uint8Array): Float32Array {
  const sampleCount = Math.floor(bytes.length / 2);
  const out = new Float32Array(sampleCount);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  for (let i = 0; i < sampleCount; i++) {
    out[i] = view.getInt16(i * 2, true) / 32768;
  }

  return out;
}
